import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import HomeSection from '../HomeSection';
import GenreTag from '../../../atoms/GenreTag';
import SeekingMemberChip from '../../../atoms/SeekingMemberChip';
import EditorialEmptyState from '../../../molecules/EditorialEmptyState';
import EditableText from '../../../molecules/EditableText';
import InstrumentExperienceEditor from '../../../molecules/InstrumentExperienceEditor';
import SeekingBandStylesEditor from '../../../molecules/SeekingBandStylesEditor';
import { UsersIcon } from '../../../atoms/icons/EditorialProfileIcons';
import {
  SEEKING_TYPES,
  isSeekingLookupType,
  normalizeSeekingPreferenceType,
} from '../../../../constants/seeking';
import './index.scss';

const UserPlusIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

const NoSeekingIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M5.6 5.6l12.8 12.8" />
  </svg>
);

const DEFAULT_ALLOWED_SEEKING_TYPES = Object.freeze([
  SEEKING_TYPES.BAND,
  SEEKING_TYPES.MEMBERS,
  SEEKING_TYPES.NONE,
]);

const normalizeAllowedSeekingTypes = (allowedTypes) => {
  const source = Array.isArray(allowedTypes) && allowedTypes.length > 0
    ? allowedTypes
    : DEFAULT_ALLOWED_SEEKING_TYPES;
  const normalizedTypes = [];

  source.forEach((type) => {
    const normalizedType = normalizeSeekingPreferenceType(type, null);
    if (normalizedType && !normalizedTypes.includes(normalizedType)) {
      normalizedTypes.push(normalizedType);
    }
  });

  return normalizedTypes.length > 0 ? normalizedTypes : DEFAULT_ALLOWED_SEEKING_TYPES;
};

const resolveDefaultSeekingType = (allowedTypes, preferredType = SEEKING_TYPES.NONE) => {
  const normalizedPreferred = normalizeSeekingPreferenceType(preferredType, SEEKING_TYPES.NONE);
  if (allowedTypes.includes(normalizedPreferred)) {
    return normalizedPreferred;
  }
  if (allowedTypes.includes(SEEKING_TYPES.NONE)) {
    return SEEKING_TYPES.NONE;
  }
  return allowedTypes[0] || SEEKING_TYPES.NONE;
};

const createEmptySeekingDraft = (type = SEEKING_TYPES.NONE) => ({
  type,
  members: [],
  bandStyles: [],
  notes: '',
  openToJam: false,
});

const normalizeSeekingDraft = (details, options = {}) => {
  const allowedTypes = normalizeAllowedSeekingTypes(options.allowedTypes);
  const defaultType = resolveDefaultSeekingType(allowedTypes, options.defaultType);
  const normalizedType = normalizeSeekingPreferenceType(details?.type, defaultType);
  const type = allowedTypes.includes(normalizedType) ? normalizedType : defaultType;
  const openToJamEnabled = options.openToJamEnabled !== false;

  if (type === SEEKING_TYPES.NONE) {
    return createEmptySeekingDraft();
  }

  return {
    type,
    members: Array.isArray(details?.members) ? details.members : [],
    bandStyles: Array.isArray(details?.bandStyles) ? details.bandStyles : [],
    notes: typeof details?.notes === 'string' ? details.notes : '',
    openToJam: openToJamEnabled && details?.openToJam !== false,
  };
};

const parseSeekingForEdit = (raw, options = {}) => {
  if (raw && typeof raw === 'object') {
    return normalizeSeekingDraft(raw, options);
  }

  if (!raw || typeof raw !== 'string') {
    return normalizeSeekingDraft(null, options);
  }
  try {
    const parsed = JSON.parse(raw);
    return normalizeSeekingDraft(parsed, options);
  } catch {
    if (options.allowPlainTextFallback === false) {
      return normalizeSeekingDraft(null, options);
    }

    const allowedTypes = normalizeAllowedSeekingTypes(options.allowedTypes);
    const fallbackType = allowedTypes.includes(SEEKING_TYPES.MEMBERS)
      ? SEEKING_TYPES.MEMBERS
      : resolveDefaultSeekingType(allowedTypes, options.defaultType);
    return normalizeSeekingDraft({ type: fallbackType, notes: raw, openToJam: true }, options);
  }
};

const hasFilledMembers = (members) => (
  Array.isArray(members) && members.some((m) => (m?.instrument || '').trim())
);

const hasFilledBandStyles = (bandStyles) => (
  Array.isArray(bandStyles) && bandStyles.some((s) => (s?.style || s?.label || '').trim())
);

const hasSeekingContent = (details) => (
  isSeekingLookupType(details?.type)
  || hasFilledMembers(details?.members)
  || hasFilledBandStyles(details?.bandStyles)
  || Boolean(details?.notes && details.notes.trim().length > 0)
);

const serializeSeekingForSave = (details, options = {}) => JSON.stringify(normalizeSeekingDraft(details, options));

const buildToggleConfig = ({ bandLabel, membersLabel, noneLabel }) => ({
  [SEEKING_TYPES.BAND]: { label: bandLabel, Icon: UsersIcon },
  [SEEKING_TYPES.MEMBERS]: { label: membersLabel, Icon: UserPlusIcon },
  [SEEKING_TYPES.NONE]: { label: noneLabel, Icon: NoSeekingIcon },
});

export default function HomeSeekingSection({
  id = 'seeking',
  eyebrow,
  title,
  side,
  lookingForLabel,
  bandLabel = 'Band',
  membersLabel = 'Members',
  noneLabel = 'Not looking',
  orLabel = 'or',
  quote,
  genresLabel,
  genres = [],
  emptyTitle,
  emptyDescription,
  editing = false,
  rawSeeking,
  seekingType,
  openToJam = true,
  openToJamLabel = "I'm open to a jam",
  bandStyles = [],
  members = [],
  membersChipLabel = 'Looking for',
  bandStylesViewLabel = 'Preferred band style',
  stylesEditorLabel = 'Band styles you’re open to',
  membersEditorLabel = 'Roles you need',
  notesPlaceholder = 'What kind of project are you open to? Bands recruiting will read this first.',
  allowedSeekingTypes = DEFAULT_ALLOWED_SEEKING_TYPES,
  allowPlainTextFallback = true,
  openToJamEnabled = true,
  fieldName = 'seeking',
  serializeDraft,
  onFieldSave,
}) {
  const normalizedAllowedSeekingTypes = useMemo(
    () => normalizeAllowedSeekingTypes(allowedSeekingTypes),
    [allowedSeekingTypes]
  );
  const defaultSeekingType = useMemo(
    () => resolveDefaultSeekingType(normalizedAllowedSeekingTypes),
    [normalizedAllowedSeekingTypes]
  );
  const seekingDraftOptions = useMemo(() => ({
    allowedTypes: normalizedAllowedSeekingTypes,
    defaultType: defaultSeekingType,
    openToJamEnabled,
    allowPlainTextFallback,
  }), [allowPlainTextFallback, defaultSeekingType, normalizedAllowedSeekingTypes, openToJamEnabled]);
  const persistedDraft = useMemo(
    () => parseSeekingForEdit(rawSeeking, seekingDraftOptions),
    [rawSeeking, seekingDraftOptions]
  );
  const [editingDraft, setEditingDraft] = useState(persistedDraft);
  const persistedDraftHasContent = hasSeekingContent(persistedDraft);

  useEffect(() => {
    if (!editing || persistedDraftHasContent) {
      setEditingDraft(persistedDraft);
    }
  }, [editing, persistedDraft, persistedDraftHasContent]);

  const activeDraft = editing ? editingDraft : persistedDraft;
  const saveSeekingDraft = useCallback(async (nextDraft) => {
    const normalizedDraft = normalizeSeekingDraft(nextDraft, seekingDraftOptions);
    setEditingDraft(normalizedDraft);
    if (onFieldSave) {
      const serializedDraft = typeof serializeDraft === 'function'
        ? serializeDraft(normalizedDraft)
        : serializeSeekingForSave(normalizedDraft, seekingDraftOptions);
      await onFieldSave(fieldName, serializedDraft);
    }
  }, [fieldName, onFieldSave, seekingDraftOptions, serializeDraft]);

  const handleNotesSave = useCallback(async (nextNotes) => {
    await saveSeekingDraft({ ...activeDraft, notes: nextNotes });
  }, [activeDraft, saveSeekingDraft]);

  const handleTypeChange = useCallback(async (nextType) => {
    const normalizedNext = normalizeSeekingPreferenceType(nextType, defaultSeekingType);
    if (!normalizedAllowedSeekingTypes.includes(normalizedNext)) return;
    if (activeDraft.type === normalizedNext) return;
    await saveSeekingDraft(
      normalizedNext === SEEKING_TYPES.NONE
        ? createEmptySeekingDraft()
        : {
          ...activeDraft,
          type: normalizedNext,
          openToJam: openToJamEnabled
            ? !(isSeekingLookupType(activeDraft.type) && activeDraft.openToJam === false)
            : false,
        }
    );
  }, [activeDraft, defaultSeekingType, normalizedAllowedSeekingTypes, openToJamEnabled, saveSeekingDraft]);

  const handleOpenToJamChange = useCallback(async (nextValue) => {
    if (!openToJamEnabled) return;
    const normalizedNext = Boolean(nextValue);
    if (activeDraft.openToJam === normalizedNext) return;
    await saveSeekingDraft({ ...activeDraft, openToJam: normalizedNext });
  }, [activeDraft, openToJamEnabled, saveSeekingDraft]);

  const handleBandStylesChange = useCallback(async (nextRows) => {
    await saveSeekingDraft({ ...activeDraft, bandStyles: nextRows });
  }, [activeDraft, saveSeekingDraft]);

  const handleMembersChange = useCallback(async (nextRows) => {
    await saveSeekingDraft({ ...activeDraft, members: nextRows });
  }, [activeDraft, saveSeekingDraft]);

  const activeType = editing
    ? normalizeSeekingPreferenceType(activeDraft.type, defaultSeekingType)
    : normalizeSeekingPreferenceType(seekingType, defaultSeekingType);
  const normalizedActiveType = normalizedAllowedSeekingTypes.includes(activeType) ? activeType : defaultSeekingType;
  const activeTypeLooksForPeople = isSeekingLookupType(normalizedActiveType);
  const activeOpenToJam = openToJamEnabled && activeTypeLooksForPeople && (editing ? activeDraft.openToJam : openToJam);
  const visibleBandStyles = editing ? activeDraft.bandStyles : bandStyles;
  const visibleMembers = editing ? activeDraft.members : members;
  const hasBandStyleContent = hasFilledBandStyles(visibleBandStyles);
  const hasMemberContent = hasFilledMembers(visibleMembers);
  const hasContent = activeTypeLooksForPeople || quote || genres.length > 0 || hasBandStyleContent || hasMemberContent;
  const seekingNotesValue = editing ? (activeDraft?.notes || '') : '';
  const toggleConfig = useMemo(
    () => buildToggleConfig({ bandLabel, membersLabel, noneLabel }),
    [bandLabel, membersLabel, noneLabel]
  );

  const renderTogglePill = (typeValue, label, IconCmp) => {
    const isActive = normalizedActiveType === typeValue;
    const classes = [
      'home-seeking__pill',
      'home-seeking__pill--toggle',
      isActive ? 'home-seeking__pill--active' : 'home-seeking__pill--inactive',
    ].join(' ');
    return (
      <button
        type="button"
        className={classes}
        aria-pressed={isActive}
        onClick={editing ? () => handleTypeChange(typeValue) : undefined}
        disabled={!editing}
      >
        <IconCmp width="20" height="20" />
        {label}
      </button>
    );
  };

  const renderToggleItems = () => normalizedAllowedSeekingTypes.map((typeValue, index) => {
    const config = toggleConfig[typeValue];
    if (!config) return null;
    return (
      <React.Fragment key={typeValue}>
        {index > 0 ? <span className="home-seeking__or" aria-hidden="true">{orLabel}</span> : null}
        {renderTogglePill(typeValue, config.label, config.Icon)}
      </React.Fragment>
    );
  });

  return (
    <HomeSection id={id} eyebrow={eyebrow} title={title} side={side} showSide={editing} className="home-seeking">
      {(hasContent || editing) ? (
        <div className="home-seeking__grid">
          <div className="home-seeking__copy">
            <div className="home-seeking__looking">
              <div className="home-seeking__looking-label">{lookingForLabel}</div>
              <div className="home-seeking__toggle">
                {editing ? (
                  <>
                    {renderToggleItems()}
                    {openToJamEnabled && activeTypeLooksForPeople ? (
                      <label className="home-seeking__jam-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(activeOpenToJam)}
                          onChange={(e) => handleOpenToJamChange(e.target.checked)}
                        />
                        <span className="home-seeking__jam-toggle-box" aria-hidden="true">
                          <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 8.5l3 3 7-7" />
                          </svg>
                        </span>
                        <span className="home-seeking__jam-toggle-label">{openToJamLabel}</span>
                      </label>
                    ) : null}
                  </>
                ) : (
                  normalizedActiveType === SEEKING_TYPES.BAND
                    ? renderTogglePill(SEEKING_TYPES.BAND, bandLabel, UsersIcon)
                    : normalizedActiveType === SEEKING_TYPES.MEMBERS
                      ? renderTogglePill(SEEKING_TYPES.MEMBERS, membersLabel, UserPlusIcon)
                      : null
                )}
              </div>
            </div>

            {editing && activeTypeLooksForPeople ? (
              <p className="home-seeking__quote home-seeking__quote--editable">
                <EditableText
                  editing
                  multiline
                  rows={4}
                  value={seekingNotesValue}
                  placeholder={notesPlaceholder}
                  ariaLabel="Seeking pitch"
                  maxLength={500}
                  onSave={handleNotesSave}
                />
              </p>
            ) : (quote ? (
              <p className="home-seeking__quote">{quote}</p>
            ) : null)}

            {/* BAND mode — styles list */}
            {normalizedActiveType === SEEKING_TYPES.BAND ? (
              editing ? (
                <div className="home-seeking__detail">
                  <div className="home-seeking__detail-label">{stylesEditorLabel}</div>
                  <SeekingBandStylesEditor
                    styles={visibleBandStyles}
                    onChange={handleBandStylesChange}
                  />
                </div>
              ) : (
                visibleBandStyles.length > 0 ? (
                  <>
                    <div className="home-seeking__detail-label">{bandStylesViewLabel}</div>
                    <div className="home-seeking__tags">
                      {visibleBandStyles
                        .filter((s) => (s.style || '').trim())
                        .map((s, i) => (
                          <GenreTag key={s.id || i} tone="cool">{s.style}</GenreTag>
                        ))}
                    </div>
                  </>
                ) : (genres.length > 0 ? (
                  <>
                    <div className="home-seeking__detail-label">{bandStylesViewLabel}</div>
                    <div className="home-seeking__tags">
                      {genres.map((g, i) => (
                        <GenreTag key={i} tone="cool">{g}</GenreTag>
                      ))}
                    </div>
                  </>
                ) : null)
              )
            ) : null}

            {/* MEMBERS mode — roles list */}
            {normalizedActiveType === SEEKING_TYPES.MEMBERS ? (
              editing ? (
                <div className="home-seeking__detail">
                  <div className="home-seeking__detail-label">{membersEditorLabel}</div>
                  <InstrumentExperienceEditor
                    experiences={visibleMembers}
                    onChange={handleMembersChange}
                    levelField="experience"
                    addLabel="Add member role"
                    instrumentPlaceholder="Pick an instrument"
                  />
                </div>
              ) : (
                visibleMembers.filter((m) => (m.instrument || '').trim()).length > 0 ? (
                  <>
                    <div className="home-seeking__detail-label">{membersChipLabel}</div>
                    <div className="home-seeking__chips">
                      {visibleMembers
                        .filter((m) => (m.instrument || '').trim())
                        .map((m, i) => (
                          <SeekingMemberChip
                            key={m.id || i}
                            instrument={m.instrument}
                            level={m.experience}
                          />
                        ))}
                    </div>
                  </>
                ) : null
              )
            ) : null}
          </div>
        </div>
      ) : (
        <EditorialEmptyState
          title={emptyTitle}
          description={emptyDescription}
        />
      )}
    </HomeSection>
  );
}

HomeSeekingSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  side: PropTypes.node,
  lookingForLabel: PropTypes.node,
  bandLabel: PropTypes.node,
  membersLabel: PropTypes.node,
  noneLabel: PropTypes.node,
  orLabel: PropTypes.node,
  quote: PropTypes.node,
  genresLabel: PropTypes.node,
  genres: PropTypes.arrayOf(PropTypes.node),
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  editing: PropTypes.bool,
  rawSeeking: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      type: PropTypes.string,
      members: PropTypes.array,
      bandStyles: PropTypes.array,
      notes: PropTypes.string,
      openToJam: PropTypes.bool,
    }),
  ]),
  seekingType: PropTypes.string,
  openToJam: PropTypes.bool,
  openToJamLabel: PropTypes.node,
  bandStyles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    style: PropTypes.string,
    note: PropTypes.string,
  })),
  members: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    instrument: PropTypes.string,
    experience: PropTypes.string,
    note: PropTypes.string,
  })),
  membersChipLabel: PropTypes.node,
  bandStylesViewLabel: PropTypes.node,
  stylesEditorLabel: PropTypes.node,
  membersEditorLabel: PropTypes.node,
  notesPlaceholder: PropTypes.string,
  allowedSeekingTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(SEEKING_TYPES))),
  allowPlainTextFallback: PropTypes.bool,
  openToJamEnabled: PropTypes.bool,
  fieldName: PropTypes.string,
  serializeDraft: PropTypes.func,
  onFieldSave: PropTypes.func,
};
